<script setup lang="ts">
// ================================
// LA DEMANDE AU FOURNISSEUR, dans la rangée « Rapport et demande » (contrat
// de l'interface v1.2 §7.2 à §7.4 ; maquette 2c jugée le 18/10/2026 ;
// DEV-2311, lot 4b, DEV-2419).
//
// Ce que le courtier doit vivre : il voit ce qui va partir, le corrige s'il
// le faut, le copie et l'envoie lui-même. Le tableau « À reporter dans la
// demande » (à ne pas casser), puis « Préparer la demande » qui déplie le
// courriel : « Avant d'envoyer » quand une condition pèse, l'aperçu modifiable
// de quatorze lignes et « Copier le courriel », toujours actif (D3 a, D16 a).
// Ni case d'accord ni choix de voix : un seul courriel, neutre (D4, D5).
// Puis « Ensuite », trois lignes (A4). Rien à demander : aucune ligne ici
// (A4), la raison de la carte le dit.
// ================================
import { computed, nextTick, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import {
    conditionsAvantDEnvoyer,
    courrielDeLaDemande,
    ENSUITE,
    libelleEnClair,
    lireDemande,
    marcheNonChiffree,
    referenceCourteEtude,
    type LigneDemande,
} from "@modules/courtage/utils/optim-turpe-v2.demande"
import {
    NIVEAU_1,
    NIVEAU_2,
    NIVEAU_3,
} from "@modules/courtage/utils/optim-turpe-v2.boutons"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            pasLaRecommandation: "Ce n'est pas la recommandation",
            colReporter: "À reporter dans la demande",
            formuleALEcran: "Formule d'acheminement (FTA)",
            puissanceUnique: "Puissance souscrite (toutes les heures)",
            colAujourdhui: "Aujourd'hui",
            colADemander: "À demander",
            nonConnue: "non connue",
            inchangee: "inchangée",
            preparer: "Préparer la demande",
            titreDemande: "Demande au fournisseur",
            apercu: "Aperçu du courriel, à relire et à modifier si besoin",
            copier: "Copier le courriel",
            copie: "Courriel copié",
            copieImpossible:
                "La copie automatique n'a pas abouti : sélectionnez le texte ci-dessus.",
            corriger: "Corriger",
            ensuite: "Ensuite",
            nonChiffreeLecture: "Pour rendre l'étude chiffrable :",
        },
    },
})

type Props = {
    etude: OptimTurpeV2Etude
    /** Le point de livraison complet (complet dans le courriel, R-T16). */
    pdl?: string | null
    /** Le site (nom du bâtiment), en fin d'objet. */
    site?: string | null
    /** « 05/10/2026 » : la date de calcul de l'étude. */
    date?: string | null
    /** La signature préremplie depuis la session (C28). */
    signataire?: string | null
    societe?: string | null
    /** Les valeurs prises par défaut qui pèsent, nommées (« Avant d'envoyer »). */
    valeursParDefaut?: string[]
    /**
     * Étude saisie ou scénario retenu contre le conseil (D17) : l'étiquette
     * « Ce n'est pas la recommandation » et la mention du courriel.
     */
    pasLaRecommandation?: boolean
    /** Le bloc du courriel est-il déplié ? Tenu par la page (ancre #rapport). */
    courrielOuvert?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    pdl: null,
    site: null,
    date: null,
    signataire: null,
    societe: null,
    valeursParDefaut: () => [],
    pasLaRecommandation: false,
    courrielOuvert: false,
})
const emit = defineEmits<{
    "update:courrielOuvert": [ouvert: boolean]
    /** « Corriger » d'une condition : le champ du raccordement. */
    corriger: []
}>()

/**
 * La place du bouton global « Aide » (rejeu du 20/10/2026, A14) : fixe en bas
 * à droite, 97 × 46 px à 32 px du bord sur un grand écran, 74 × 35 px à 24 px
 * sous 640 px (mesurés). Le tableau garde à droite une marge un peu plus
 * large que lui, 8,5 rem (136 px) ou 6,5 rem (104 px) : aucune valeur ne
 * passe dessous, quel que soit le défilement. Le bouton n'est pas touché.
 */
const AIDE_RESERVE_TABLEAU = "pr-[6.5rem] sm:pr-[8.5rem]"

const getDemande = computed(() => lireDemande(props.etude))
const getDossier = computed(() =>
    getDemande.value?.etat === "demande" ? getDemande.value : null
)
/** Les lignes du tableau ; le barème et la référence sont une note. */
const getLignes = computed<LigneDemande[]>(
    () => getDossier.value?.lignes.filter((l) => !l.reference) ?? []
)
const getSegment = computed(
    () => props.etude.compteur?.segment ?? props.etude.demande?.segment ?? null
)
const getIdentifiant = computed(
    () => props.etude.demande?.identifiant_etude ?? props.etude._id ?? null
)
const getReference = computed(() =>
    getIdentifiant.value ? referenceCourteEtude(getIdentifiant.value) : null
)
const getMarcheNonChiffree = computed(() => marcheNonChiffree(props.etude))
const getConditions = computed(() =>
    conditionsAvantDEnvoyer(props.etude, props.valeursParDefaut)
)

/** Le courriel composé (contrat §7.3), valeur initiale de l'aperçu. */
const getCourriel = computed(() =>
    getDossier.value
        ? courrielDeLaDemande(getDossier.value, {
              pdl: props.pdl,
              site: props.site,
              segment: getSegment.value,
              titulaire: props.etude.site_du_courriel?.titulaire ?? null,
              adresse: props.etude.site_du_courriel?.adresse ?? null,
              signataire: props.signataire,
              societe: props.societe,
              date: props.date,
              identifiant: getIdentifiant.value,
              repliKva:
                  props.etude.demande?.repli_palier_usuel?.puissance_kva ??
                  null,
              pasLaRecommandation: props.pasLaRecommandation,
          })
        : ""
)

/** L'aperçu modifiable : une correction n'est pas enregistrée (§7.2). */
const apercu = ref(getCourriel.value)
const copie = ref<"ok" | "echec" | null>(null)
const zone = ref<{ $el?: HTMLTextAreaElement } | HTMLTextAreaElement | null>(
    null
)
watch(getCourriel, (texte) => {
    apercu.value = texte
    copie.value = null
})

/**
 * « Heures de pointe (PTE) » : le poste en clair, son code entre
 * parenthèses ; en C5, « Puissance souscrite (toutes les heures) ».
 */
function libelleALEcran(libelle: string): string {
    if (libelle === "Formule tarifaire d'acheminement")
        return t("formuleALEcran")
    if (libelle === "Puissance souscrite") return t("puissanceUnique")
    const clair = libelleEnClair(libelle)
    const m = /^Puissance souscrite en (.+)$/.exec(clair)
    return m ? m[1].charAt(0).toUpperCase() + m[1].slice(1) : clair
}

/** « longue utilisation à pointe fixe (HTALU5) » pour « formule longue … ». */
function sansMot(valeur: string): string {
    return valeur.replace(/^formule /, "")
}

/**
 * La zone de texte native, pour la sélectionner quand la copie échoue. Le
 * composant peut rendre un enveloppe autour du `<textarea>` : on le cherche
 * dedans, et rien n'est sélectionné quand il n'existe pas.
 */
function elementDeLaZone(): HTMLTextAreaElement | null {
    const z = zone.value as { $el?: Element } | Element | null
    const el = z instanceof Element ? z : (z?.$el ?? null)
    if (!el) return null
    if (el instanceof HTMLTextAreaElement) return el
    return typeof el.querySelector === "function"
        ? el.querySelector("textarea")
        : null
}

async function onCopier(): Promise<void> {
    try {
        await navigator.clipboard.writeText(
            apercu.value.replace(/[\u00a0\u202f]/g, " ")
        )
        copie.value = "ok"
    } catch {
        copie.value = "echec"
        await nextTick()
        const el = elementDeLaZone()
        el?.focus()
        el?.select()
    }
}
</script>

<template>
    <!-- Économie non chiffrée : la marche à suivre servie, aucune valeur. -->
    <section
        v-if="getMarcheNonChiffree.length > 0"
        class="flex flex-col gap-2"
        data-testid="optim-turpe-v2-demande-non-chiffree"
    >
        <p class="text-sm text-surface-700">{{ t("nonChiffreeLecture") }}</p>
        <ol class="list-decimal pl-5 text-sm text-primary-700">
            <li v-for="etape in getMarcheNonChiffree" :key="etape">
                {{ etape }}
            </li>
        </ol>
    </section>

    <section
        v-else-if="getDossier"
        class="optim-turpe-v2-demande flex flex-col gap-4"
        data-testid="optim-turpe-v2-demande"
    >
        <BaseTag
            v-if="pasLaRecommandation"
            :value="t('pasLaRecommandation')"
            severity="secondary"
            class="w-fit"
            data-testid="optim-turpe-v2-demande-pas-la-recommandation"
        />
        <div class="flex flex-col gap-1">
            <!-- Rejeu du 20/10/2026 (A14) : le bouton global « Aide », fixe en
                 bas à droite, recouvrait la dernière valeur « À demander ». Le
                 tableau lui laisse sa place à droite, comme le pied collant
                 du formulaire (`AIDE_RESERVE_TABLEAU`). -->
            <div
                class="overflow-x-auto"
                :class="AIDE_RESERVE_TABLEAU"
                data-testid="optim-turpe-v2-demande-tableau"
            >
                <!-- Sous 640 px, la colonne « Aujourd'hui » disparaît. -->
                <table class="w-full text-sm sm:min-w-[30rem]">
                    <thead>
                        <tr class="text-left text-sm text-surface-500">
                            <th class="py-1 pr-3 font-normal">
                                {{ t("colReporter") }}
                            </th>
                            <th
                                class="hidden py-1 pr-3 text-right font-normal sm:table-cell"
                                data-testid="optim-turpe-v2-demande-col-aujourdhui"
                            >
                                {{ t("colAujourdhui") }}
                            </th>
                            <th class="py-1 text-right font-normal">
                                {{ t("colADemander") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="ligne in getLignes"
                            :key="ligne.libelle"
                            class="border-t border-surface-100"
                            data-testid="optim-turpe-v2-demande-ligne"
                        >
                            <th class="py-1 pr-3 text-left font-normal">
                                {{ libelleALEcran(ligne.libelle) }}
                            </th>
                            <td
                                class="hidden py-1 pr-3 text-right text-surface-700 sm:table-cell"
                            >
                                {{
                                    ligne.aujourdhui
                                        ? sansMot(ligne.aujourdhui)
                                        : t("nonConnue")
                                }}
                            </td>
                            <td
                                class="py-1 text-right"
                                :class="
                                    ligne.inchangee
                                        ? 'text-surface-700'
                                        : 'font-semibold text-primary-900'
                                "
                            >
                                {{
                                    ligne.inchangee
                                        ? t("inchangee")
                                        : sansMot(ligne.valeur)
                                }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p
                v-if="getReference"
                class="text-xs text-surface-500"
                data-testid="optim-turpe-v2-demande-reference"
            >
                {{ getReference }}
            </p>
        </div>

        <!-- Rangée ouverte par son en-tête : le courriel reste replié
             derrière « Préparer la demande » ; ouverte par l'ancre, il est
             déplié (D16). -->
        <BaseButton
            v-if="!courrielOuvert"
            v-bind="NIVEAU_2"
            :label="t('preparer')"
            icon="pi pi-envelope"
            class="w-fit"
            data-testid="optim-turpe-v2-demande-preparer"
            @click="emit('update:courrielOuvert', true)"
        />
        <div
            v-else
            id="courriel"
            class="flex scroll-mt-6 flex-col gap-3"
            data-testid="optim-turpe-v2-courriel"
        >
            <h3 class="text-sm font-medium text-primary-800">
                {{ t("titreDemande") }}
            </h3>
            <BaseMessage
                v-if="getConditions.length > 0"
                severity="info"
                size="small"
                data-testid="optim-turpe-v2-avant-d-envoyer"
            >
                <ul class="flex flex-col gap-1">
                    <li
                        v-for="condition in getConditions"
                        :key="condition.texte"
                        data-testid="optim-turpe-v2-condition"
                    >
                        {{ condition.texte }}
                        <BaseButton
                            v-if="condition.raccordement"
                            v-bind="NIVEAU_3"
                            :label="t('corriger')"
                            data-testid="optim-turpe-v2-condition-corriger"
                            @click="emit('corriger')"
                        />
                    </li>
                </ul>
            </BaseMessage>
            <label
                for="optim-turpe-v2-apercu-courriel"
                class="text-sm text-surface-700"
                >{{ t("apercu") }}</label
            >
            <BaseTextarea
                id="optim-turpe-v2-apercu-courriel"
                ref="zone"
                v-model="apercu"
                rows="14"
                class="w-full text-sm"
                data-testid="optim-turpe-v2-apercu-courriel"
            />
            <div class="flex flex-col items-start gap-1">
                <BaseButton
                    v-bind="NIVEAU_1"
                    :label="t('copier')"
                    icon="pi pi-copy"
                    class="w-fit"
                    data-testid="optim-turpe-v2-copier-courriel"
                    @click="onCopier"
                />
                <p
                    class="min-h-4 text-xs"
                    :class="
                        copie === 'echec'
                            ? 'text-[var(--p-orange-700)]'
                            : 'text-surface-500'
                    "
                    role="status"
                    data-testid="optim-turpe-v2-copie-courriel-statut"
                >
                    {{
                        copie === "ok"
                            ? t("copie")
                            : copie === "echec"
                              ? t("copieImpossible")
                              : ""
                    }}
                </p>
            </div>
        </div>

        <!-- « Ensuite » (A4) : trois lignes, avant le rapport. -->
        <div class="flex flex-col gap-1" data-testid="optim-turpe-v2-ensuite">
            <h3 class="text-sm font-medium text-primary-800">
                {{ t("ensuite") }}
            </h3>
            <ol class="list-decimal pl-5 text-sm text-surface-700">
                <li v-for="ligne in ENSUITE" :key="ligne">{{ ligne }}</li>
            </ol>
        </div>
    </section>
</template>
